import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { Subject, takeUntil } from 'rxjs';
import { GradeStatus } from '../../../../models/grade.school.model';
import { SchoolMember } from '../../../../models/member.school.model';
import { UtilsService } from '../../../../services/utils.service';
import * as gradeActions from '../../../../state/actions/grade.school.actions';
import { AppState } from '../../../../state/appstate';
import { BtnSubmitComponent } from '../../../components/buttons/btn-submit/btn-submit.component';

@Component({
  selector: 'app-dlg-upsert-grade',
  standalone: true,
  imports: [ReactiveFormsModule, BtnSubmitComponent],
  templateUrl: './dlg-upsert-grade.component.html',
  styleUrl: './dlg-upsert-grade.component.css',
})
export class DlgUpsertGradeComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<DlgUpsertGradeComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  private dialog: MatDialog = inject(MatDialog);
  store: Store<AppState> = inject(Store<AppState>);
  fb = inject(FormBuilder);
  private actions$: Actions = inject(Actions);
  logger: NGXLogger = inject(NGXLogger);
  utilsService = inject(UtilsService);

  pageForm: FormGroup = new FormGroup({});

  title: string = 'Create Grade';
  description: string = 'Enter Name of Grade';
  action: string = 'Create';
  faculty: SchoolMember[] | undefined;

  working: boolean = false;

  exit$: Subject<void> = new Subject();

  constructor() {
    this.pageForm = this.fb.group({
      gradeName: new FormControl(this.data.grade?.gradeName ?? '', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(100),
      ]),
      gradeAvgAge: new FormControl(
        this.data.grade?.gradeAvgAge ?? '',
        Validators.required,
      ),
      // teacherId: new FormControl(this.data.grade?.teacherId ?? 'unassigned'),
    });

    this.title = this.data.grade?.id ? ' Update Grade' : 'Create Grade';
    this.description = this.data.grade?.id
      ? 'Update grade details'
      : 'Enter New Grade details';
    this.action = this.data.grade?.id ? 'Update' : 'Create';
    this.faculty = this.data.faculty;
  }

  ngOnInit(): void {
    // Subscribe to updateSuccess action
    this.actions$
      .pipe(ofType(gradeActions.updateSuccess), takeUntil(this.exit$))
      .subscribe(() => {
        // console.log('Profile loaded successfully');
        this.working = false;
        this.dialogRef.close(true);
      });

    // Subscribe to updateFailure action (optional, for error handling)
    this.actions$
      .pipe(ofType(gradeActions.updateFailure), takeUntil(this.exit$))
      .subscribe(({ error }) => {
        this.logger.error('Grade update failed', error);
        this.working = false;
        this.utilsService.onShowAlert(error);
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  submit(): void {
    this.pageForm.markAllAsTouched();
    this.pageForm.updateValueAndValidity();

    if (this.pageForm.invalid) return;
    if (
      this.data.groupId == undefined ||
      (this.data.grade?.gradeYear == undefined &&
        this.data.academicYear == undefined)
    ) {
      this.logger.debug(
        `Form Invalid: ${this.pageForm.invalid} GroupId: ${this.data.groupId}, ${this.data.grade?.gradeYear}, ${this.data.academicYear}`,
      );
      this.utilsService.onShowAlert(
        {
          code: 'ERR_FORM_INVALID',
          title: 'Pre-requisites not met',
          message:
            'Invalid form data or pre-requisite fields unavailable. Please try again later',
        },
        false,
      );
      return;
    }

    this.working = true;

    const result = this.pageForm.value;
    let grade: any = {
      gradeName: result.gradeName,
      gradeStatus: GradeStatus.active,
      gradeYear: Number(this.data.grade?.gradeYear ?? this.data.academicYear),
      gradeAvgAge: result.gradeAvgAge,
      groupId: this.data.groupId,
    };

    if (this.data.grade?.id) {
      this.store.dispatch(
        gradeActions.update({
          id: this.data.grade.id,
          grade,
        }),
      );
    } else {
      this.store.dispatch(gradeActions.create({ grade }));
    }
  }
}
